import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from '@/locales/en/common.json';
import frCommon from '@/locales/fr/common.json';
import deCommon from '@/locales/de/common.json';
import esCommon from '@/locales/es/common.json';

import enAuth from '@/locales/en/auth.json';
import frAuth from '@/locales/fr/auth.json';
import deAuth from '@/locales/de/auth.json';
import esAuth from '@/locales/es/auth.json';

import enLanding from '@/locales/en/landing.json';
import frLanding from '@/locales/fr/landing.json';
import deLanding from '@/locales/de/landing.json';
import esLanding from '@/locales/es/landing.json';

import enDashboard from '@/locales/en/dashboard.json';
import frDashboard from '@/locales/fr/dashboard.json';
import deDashboard from '@/locales/de/dashboard.json';
import esDashboard from '@/locales/es/dashboard.json';

// English-only for now; fr/de/es fall back via i18next fallbackLng to en.
import enLandingZones from '@/locales/en/landingZones.json';
import enSettingsAre from '@/locales/en/settingsAre.json';

export const supportedLanguages = ['en', 'fr', 'de', 'es'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español'
};

export const languageFlags: Record<SupportedLanguage, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  de: '🇩🇪',
  es: '🇪🇸'
};

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      auth: enAuth,
      landing: enLanding,
      dashboard: enDashboard,
      landingZones: enLandingZones,
      settingsAre: enSettingsAre
    },
    fr: {
      common: frCommon,
      auth: frAuth,
      landing: frLanding,
      dashboard: frDashboard,
      landingZones: enLandingZones,
      settingsAre: enSettingsAre
    },
    de: {
      common: deCommon,
      auth: deAuth,
      landing: deLanding,
      dashboard: deDashboard,
      landingZones: enLandingZones,
      settingsAre: enSettingsAre
    },
    es: {
      common: esCommon,
      auth: esAuth,
      landing: esLanding,
      dashboard: esDashboard,
      landingZones: enLandingZones,
      settingsAre: enSettingsAre
    }
  },
  // Start deterministic (en) on both SSR and client so the first render
  // matches. applyStoredLanguage() is called from _app.tsx after hydration
  // to switch to the user's preferred language.
  lng: 'en',
  fallbackLng: 'en',
  defaultNS: 'common',
  supportedLngs: supportedLanguages as unknown as string[],
  interpolation: { escapeValue: false },
  showSupportNotice: false
});

const LANGUAGE_STORAGE_KEY = 'eezi-language';

const isSupportedLanguage = (value: string | null | undefined): value is SupportedLanguage =>
  !!value && (supportedLanguages as readonly string[]).includes(value);

export const applyStoredLanguage = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored)) {
      if (i18n.language !== stored) void i18n.changeLanguage(stored);
      return;
    }
    const navLang = window.navigator.language?.slice(0, 2);
    if (isSupportedLanguage(navLang) && i18n.language !== navLang) {
      void i18n.changeLanguage(navLang);
    }
  } catch {
    // localStorage access can throw in privacy modes; default to 'en'.
  }
};

/**
 * Switches language and remembers the choice.
 */
export const setLanguage = (lng: SupportedLanguage): void => {
  if (i18n.language !== lng) void i18n.changeLanguage(lng);
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // localStorage access can throw in privacy modes; the switch still applies.
  }
};

export default i18n;
